/**
 * Readers for single values in hub JSON, used by model constructors to copy named fields from `unknown` data.
 *
 * Read-modify-write rule: a writable field the hub didn't send stays `undefined`, and an explicit `null`
 * stays `null`. Never default a writable field to '' / null / 0, or a form built from the model could
 * blank a value the hub redacted.
 */

/** A string as sent, `null` when the hub sent `null`, otherwise `undefined`. */
export function nullableString(value: unknown): string | null | undefined {
  if (value === null) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/** A finite number, else 0. Only for read-only display totals, never for writable fields. */
export function countOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** An id as a string ('' when missing). */
export function idString(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

/** A valid `Date`, else `null` (never an Invalid Date). */
export function dateOrNull(value: unknown): Date | null {
  if (!(value instanceof Date) && typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** A finite number as sent, `null` when the hub sent `null`, otherwise `undefined`. */
export function nullableNumber(value: unknown): number | null | undefined {
  if (value === null) {
    return null;
  }
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** A boolean as sent, `null` when the hub sent `null`, otherwise `undefined`. */
export function nullableBoolean(value: unknown): boolean | null | undefined {
  if (value === null) {
    return null;
  }
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * A shallow copy of a request body without its `undefined` values, so only the keys that are set are sent.
 * `null` is kept: it tells the hub to clear that field.
 */
export function omitUndefined<T extends object>(fields: T): T {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) {
      body[key] = value;
    }
  }
  return body as T;
}

/** A string array as sent (non-strings dropped), `null` when the hub sent `null`, otherwise `undefined`. */
export function nullableStringArray(value: unknown): string[] | null | undefined {
  if (value === null) {
    return null;
  }
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : undefined;
}

/** A string array as sent (non-strings dropped), else `[]`. Only for read-only lists, never for writable fields. */
export function stringArrayOrEmpty(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

/**
 * A decimal column as a number. Postgres `numeric` / `decimal` values reach the hub as numeric strings (it
 * registers no numeric type parser), so `"499.65"` and `499.65` both read as `499.65`. Same rule as
 * `nullableNumber`: `null` when the hub sent `null`, otherwise `undefined`. Never defaults to 0.
 */
export function nullableDecimal(value: unknown): number | null | undefined {
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return nullableNumber(value);
}

/** A list of `read` items; `[]` when the hub sent no array. Only for read-only lists. */
export function listOf<T>(value: unknown, read: (item: unknown) => T): T[] {
  return Array.isArray(value) ? value.map(read) : [];
}
